import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Dumbbell, 
  Salad, 
  HeartPulse, 
  Activity, 
  Flame, 
  Building, 
  Sparkles,
  Users,
  Calendar,
  ArrowRight,
  Trophy,
  Clock,
  Zap,
  Music4,
  Sword
} from "lucide-react";
 import { getProgramName } from "@/config/programBranding";

const categories = [
  {
    id: "trainer",
    icon: Dumbbell,
    label: getProgramName("trainer"),
    subtitle: "Entrenadores",
    path: "/funnel/entrenador",
    color: "from-emerald-500 to-green-600",
    bgColor: "bg-emerald-500/10",
    textColor: "text-emerald-600",
  },
  {
    id: "nutritionist",
    icon: Salad,
    label: getProgramName("nutritionist"),
    subtitle: "Nutricionistas",
    path: "/funnel/nutricionista",
    color: "from-green-500 to-emerald-600",
    bgColor: "bg-green-500/10",
    textColor: "text-green-600",
  },
  {
    id: "physiotherapist",
    icon: HeartPulse,
    label: getProgramName("physiotherapist"),
    subtitle: "Fisioterapeutas",
    path: "/funnel/fisioterapeuta",
    color: "from-cyan-500 to-teal-600",
    bgColor: "bg-cyan-500/10",
    textColor: "text-cyan-600",
  },
  {
    id: "yoga",
    icon: Activity,
    label: getProgramName("yoga"),
    subtitle: "Yoga",
    path: "/funnel/yoga",
    color: "from-purple-500 to-violet-600",
    bgColor: "bg-purple-500/10",
    textColor: "text-purple-600",
  },
  {
    id: "crossfit",
    icon: Flame,
    label: getProgramName("crossfit"),
    subtitle: "CrossFit",
    path: "/funnel/crossfit",
    color: "from-orange-500 to-red-600",
    bgColor: "bg-orange-500/10",
    textColor: "text-orange-600",
  },
  {
    id: "gym",
    icon: Building,
    label: getProgramName("gym"),
    subtitle: "Gimnasios",
    path: "/funnel/gimnasio",
    color: "from-green-500 to-emerald-600",
    bgColor: "bg-green-500/10",
    textColor: "text-green-600",
  },
  {
    id: "pilates",
    icon: Activity,
    label: getProgramName("pilates"),
    subtitle: "Pilates",
    path: "/funnel/pilates",
    color: "from-teal-500 to-cyan-600",
    bgColor: "bg-teal-500/10",
    textColor: "text-teal-600",
  },
  {
    id: "dance",
    icon: Music4,
    label: getProgramName("dance"),
    subtitle: "Baile",
    path: "/funnel/dance",
    color: "from-pink-500 to-rose-600",
    bgColor: "bg-pink-500/10",
    textColor: "text-pink-600",
  },
  {
    id: "martial_arts",
    icon: Sword,
    label: getProgramName("martial_arts"),
    subtitle: "Artes Marciales",
    path: "/funnel/martial-arts",
    color: "from-red-500 to-orange-600",
    bgColor: "bg-red-500/10",
    textColor: "text-red-600",
  },
];

export const FunnelCTASection = () => {
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);

  return (
    <section className="py-16 md:py-24 relative overflow-hidden bg-gradient-to-br from-primary/5 via-accent/10 to-primary/5">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header - More Prominent */}
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-full text-sm font-semibold mb-6 shadow-lg shadow-primary/25 animate-pulse">
            <Sparkles className="w-4 h-4" />
            🎉 90 días de prueba GRATIS
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 tracking-tight">
            ¿Eres Profesional del{" "}
            <span className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
              Fitness o Bienestar
            </span>?
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            Únete a <strong className="text-foreground">+2,500 profesionales</strong> que ya automatizan su negocio. 
            Elige tu especialidad y descubre tu plan personalizado.
          </p>
        </div>

        {/* Category Cards - More Prominent Grid */}
        <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-9 gap-3 md:gap-4 max-w-7xl mx-auto mb-14">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={cat.path}
              onMouseEnter={() => setHoveredCategory(cat.id)}
              onMouseLeave={() => setHoveredCategory(null)}
              className="group block"
            >
              <Card className={`h-full border-2 transition-all duration-300 bg-card/80 backdrop-blur-sm ${
                hoveredCategory === cat.id 
                  ? "border-primary shadow-xl shadow-primary/20 scale-105 -translate-y-1" 
                  : "border-border hover:border-primary/50 hover:shadow-lg"
              }`}>
                <CardContent className="flex flex-col items-center justify-center p-3 sm:p-4 text-center min-h-[130px]">
                  <div className={`p-3 rounded-xl mb-2 ${cat.bgColor} transition-all group-hover:scale-110 group-hover:shadow-lg`}>
                    <cat.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${cat.textColor}`} />
                  </div>
                  <span className="text-xs sm:text-sm font-bold leading-tight">
                    {cat.label}
                  </span>
                  <span className="text-[10px] text-muted-foreground mt-0.5">
                    {cat.subtitle}
                  </span>
                  <div className={`mt-2 flex items-center gap-1 text-[10px] font-medium text-primary transition-all ${
                    hoveredCategory === cat.id 
                      ? "opacity-100 translate-x-0" 
                      : "opacity-0 -translate-x-2"
                  }`}>
                    Ver plan
                    <ArrowRight className="w-2 h-2" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Stats Row - Below Cards */}
        <div className="flex flex-wrap justify-center gap-6 md:gap-10 mb-14 py-6 px-4 bg-card/50 backdrop-blur-sm rounded-2xl border max-w-4xl mx-auto">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-lg font-bold">+2,500</p>
              <p className="text-xs text-muted-foreground">profesionales</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Clock className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-lg font-bold">15+ horas</p>
              <p className="text-xs text-muted-foreground">ahorradas/mes</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Trophy className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-lg font-bold">0%</p>
              <p className="text-xs text-muted-foreground">comisiones</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Zap className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-lg font-bold">5 min</p>
              <p className="text-xs text-muted-foreground">para empezar</p>
            </div>
          </div>
        </div>

        {/* Booking System CTA - Enhanced */}
        <div className="max-w-4xl mx-auto">
          <Card className="border-2 border-primary/30 bg-gradient-to-br from-card via-primary/5 to-accent/10 shadow-xl overflow-hidden relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <CardContent className="p-8 md:p-10 text-center relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-full text-sm font-semibold mb-6 shadow-lg">
                <Calendar className="w-4 h-4" />
                SISTEMA DE RESERVAS PRO
              </div>
              <h3 className="text-2xl md:text-3xl font-bold mb-4">
                ¿Ya tienes presencia online? <br className="hidden sm:block" />
                <span className="text-primary">Añade Reservas Automáticas</span>
              </h3>
              <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
                Automatiza tus citas, acepta pagos online y olvídate de los no-shows. 
                <strong className="text-foreground"> Integración en 5 minutos.</strong>
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-lg px-8 h-14 shadow-lg shadow-primary/25">
                  <Link to="/funnel/reservas">
                    <Calendar className="w-5 h-5 mr-2" />
                    Ver Demo del Sistema
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="text-lg px-8 h-14">
                  <Link to="/sistema-reservas">
                    Más Información
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Trust Badge */}
        <div className="text-center mt-10">
          <p className="text-sm text-muted-foreground font-medium">
            ✓ Sin compromiso · ✓ Sin tarjeta de crédito · ✓ Cancela cuando quieras
          </p>
        </div>
      </div>
    </section>
  );
};
